/**
 * API client — one place that knows the /api/v1 contract, error bodies and downloads.
 * Sessions ride on the HttpOnly cookie; a 401 anywhere signs the app out (see session.tsx).
 */
export type Query = Record<string, string | number | boolean | undefined | null | Array<string | number>>;

export interface ValidationIssue { field: string; code: string; message: string }

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly issues: ValidationIssue[] = [], readonly details?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
  /** field → message for forms */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.issues.map((i) => [i.field, i.message]));
  }
}

const listeners = new Set<(e: ApiError) => void>();
/** Subscribe to authentication failures (the session provider signs out). */
export function onUnauthenticated(fn: (e: ApiError) => void): () => void { listeners.add(fn); return () => listeners.delete(fn); }

function qs(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) for (const x of v) p.append(k, String(x)); else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const url = `/api/v1/${path.replace(/^\/+/, '')}${qs(query)}`;
  let res: Response;
  try {
    res = await fetch(url, { method, headers: body !== undefined ? { 'content-type': 'application/json' } : {}, body: body !== undefined ? JSON.stringify(body) : undefined, credentials: 'same-origin' });
  } catch (e) {
    throw new ApiError(0, 'network', `Cannot reach the server (${(e as Error).message})`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: 'bad_response', message: text.slice(0, 200) }; }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; message?: string; issues?: ValidationIssue[]; details?: unknown };
    const err = new ApiError(res.status, d.error ?? 'error', d.message ?? res.statusText, d.issues ?? [], d.details);
    if (res.status === 401 && !path.startsWith('auth/login')) for (const l of listeners) l(err);
    throw err;
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, body ?? {}, query),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
  /** URL for a browser download / new-tab open (print, file content) */
  downloadUrl: (path: string, query?: Query) => `/api/v1/${path.replace(/^\/+/, '')}${qs({ ...query, download: 1 })}`,
};

/** Saves text or bytes as a file from the browser. */
export function saveFile(name: string, content: string | Uint8Array, mime = 'text/plain;charset=utf-8'): void {
  const blob = new Blob([content as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function toBase64(bytes: ArrayBuffer): string {
  let s = ''; const b = new Uint8Array(bytes);
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
}
